import React, { useEffect, useState, useRef } from "react";
import MDBox from "components/MDBox";
import PowerSettingsNewIcon from "@mui/icons-material/PowerSettingsNew";
import SpeedIcon from "@mui/icons-material/Speed";
import CloseIcon from "@mui/icons-material/Close";
import IconButton from "@mui/material/IconButton";
import { useMediaQuery } from "@mui/material";
import { useTheme } from "@mui/material/styles";
import { FONT_SIZE_PX, RADIUS, SHADOW, BRAND } from "./theme/uiTokens";

/**
 * Helper function to draw perfect, smooth SVG arcs
 * Used here for the specific redline track on the gauge
 */
const describeArc = (x, y, radius, startAngle, endAngle) => {
  const polarToCartesian = (centerX, centerY, r, angleInDegrees) => {
    const angleInRadians = (angleInDegrees - 90) * (Math.PI / 180.0);
    return {
      x: centerX + r * Math.cos(angleInRadians),
      y: centerY + r * Math.sin(angleInRadians),
    };
  };

  const start = polarToCartesian(x, y, radius, endAngle);
  const end = polarToCartesian(x, y, radius, startAngle);
  const largeArcFlag = endAngle - startAngle <= 180 ? "0" : "1";

  return ["M", start.x, start.y, "A", radius, radius, 0, largeArcFlag, 0, end.x, end.y].join(" ");
};

/**
 * RealisticSpeedometer: Photorealistic gauge. Accepts a `size` prop so the
 * caller can scale it down on small screens without redrawing the geometry -
 * the SVG viewBox stays fixed (0 0 200 200) and only the rendered box size
 * changes, so every tick/label stays crisp and proportional at any size.
 */
const RealisticSpeedometer = ({ value = 0, max = 200, size = 220 }) => {
  const [displayValue, setDisplayValue] = useState(0);
  const requestRef = useRef();

  const animateSpeed = () => {
    setDisplayValue((prev) => {
      const target = Math.min(value, max);
      const diff = target - prev;

      // dynamic easing
      const easingFactor = 0.02;
      const step = diff * easingFactor;

      // snap when very close
      if (Math.abs(diff) < 0.1) {
        return target;
      }

      return prev + step;
    });

    requestRef.current = requestAnimationFrame(animateSpeed);
  };

  useEffect(() => {
    requestRef.current = requestAnimationFrame(animateSpeed);
    return () => cancelAnimationFrame(requestRef.current);
  }, [value, max]);

  const center = 100;

  // Calculate Needle Angle: 0 km/h is at -135deg, Max (200) is at +135deg. (0deg is straight up)
  const angle = (displayValue - max / 2) * (135 / (max / 2));

  // Generate Ticks and Number Labels
  const ticks = [];
  for (let i = 0; i <= max; i += 10) {
    const isMajor = i % 20 === 0;
    const tickAngleDeg = (i - max / 2) * (135 / (max / 2));
    const rad = (tickAngleDeg - 90) * (Math.PI / 180);

    const outerR = 92;
    const innerR = isMajor ? 80 : 86;

    const x1 = center + outerR * Math.cos(rad);
    const y1 = center + outerR * Math.sin(rad);
    const x2 = center + innerR * Math.cos(rad);
    const y2 = center + innerR * Math.sin(rad);

    ticks.push(
      <line
        key={`tick-${i}`}
        x1={x1}
        y1={y1}
        x2={x2}
        y2={y2}
        stroke="#ffffff"
        strokeWidth={isMajor ? 2.5 : 1.5}
        strokeLinecap="round"
      />
    );

    // Add Text only on major intervals
    if (isMajor) {
      const textR = 64;
      const tx = center + textR * Math.cos(rad);
      const ty = center + textR * Math.sin(rad);
      ticks.push(
        <text
          key={`label-${i}`}
          x={tx}
          y={ty}
          fill="#ffffff"
          fontSize={FONT_SIZE_PX.caption}
          fontWeight="bold"
          fontFamily="Arial, Helvetica, sans-serif"
          textAnchor="middle"
          dominantBaseline="central"
        >
          {i}
        </text>
      );
    }
  }

  // Calculate coordinates for the concentric red inner arc (From 80 to 160)
  const redArcStartAngle = (80 - max / 2) * (135 / (max / 2));
  const redArcEndAngle = (160 - max / 2) * (135 / (max / 2));
  const redArcPath = describeArc(center, center, 48, redArcStartAngle, redArcEndAngle);

  return (
    <MDBox sx={{ width: size, height: size, margin: "0 auto", position: "relative" }}>
      <svg
        viewBox="0 0 200 200"
        style={{ width: "100%", height: "100%", filter: "drop-shadow(0px 10px 20px rgba(0,0,0,0.5))" }}
      >
        {/* Dark Matte Background Face of the Speedometer */}
        <circle cx={center} cy={center} r="98" fill="#111111" stroke="#222222" strokeWidth="2" />
        <circle cx={center} cy={center} r="96" fill="#0a0a0a" />

        {/* Thin Red Arc (80 to 160 range) */}
        <path d={redArcPath} fill="none" stroke={BRAND.danger} strokeWidth="1" strokeLinecap="round" />

        {/* Ticks & Numbers */}
        {ticks}

        {/* Digital Readout */}
        <text
          x={center}
          y="158"
          fill="#ffffff"
          fontSize={FONT_SIZE_PX.title}
          fontWeight="bold"
          fontFamily="Arial, Helvetica, sans-serif"
          textAnchor="middle"
        >
          {Math.round(displayValue)}
        </text>
        <text
          x={center}
          y="170"
          fill="#888888"
          fontSize={FONT_SIZE_PX.micro}
          fontWeight="bold"
          fontFamily="Arial, Helvetica, sans-serif"
          textAnchor="middle"
          letterSpacing="1"
        >
          KM/H
        </text>

        {/* Animated Solid Red Needle */}
        <g transform={`rotate(${angle} ${center} ${center})`}>
          <line
            x1={center}
            y1={center}
            x2={center}
            y2="22"
            stroke={BRAND.danger}
            strokeWidth="3.5"
            strokeLinecap="round"
            filter="drop-shadow(0px 2px 4px rgba(0,0,0,0.6))"
          />
        </g>

        {/* Central Cap */}
        <circle cx={center} cy={center} r="12" fill="#1a1a1a" />
        <circle cx={center} cy={center} r="10" fill="#111111" stroke="#2a2a2a" strokeWidth="1" />
      </svg>
    </MDBox>
  );
};

/**
 * VehicleDashboard: Floating speedometer + ignition badge for the selected
 * vehicle. Scales down and pulls in from the edge on small screens so it
 * never gets clipped by the viewport or covers the whole map on a phone.
 */
const VehicleDashboard = ({ vehicle }) => {
  const [isOpen, setIsOpen] = useState(true);
  const theme = useTheme();
  const isMobile = useMediaQuery(theme.breakpoints.down("sm"));

  if (!vehicle) return null;

  // Convert m/s to km/h assuming vehicle.speed is passed in m/s
  const speed = Number(vehicle.speed * 3.6) || 0;
  const isIgnitionOn = vehicle.ignition === "ON";

  const gaugeSize = isMobile ? 168 : 220;
  const panelWidth = isMobile ? 200 : 260;
  const edgeOffset = isMobile ? 12 : 30;

  return (
    <MDBox
      sx={{
        position: "absolute",
        bottom: edgeOffset,
        right: edgeOffset,
        zIndex: 2000,
        transition: "transform 0.4s cubic-bezier(0.4, 0, 0.2, 1)",
      }}
    >
      {isOpen ? (
        <MDBox
          sx={{
            width: panelWidth,
            background: "transparent",
            padding: 1.25,
            display: "flex",
            flexDirection: "column",
            alignItems: "center",
          }}
        >
          {/* Top Close Button */}
          <MDBox width="100%" display="flex" justifyContent="flex-end" mb={1}>
            <IconButton
              size="small"
              onClick={() => setIsOpen(false)}
              sx={{
                width: 28,
                height: 28,
                color: "#cfcfcf",
                backgroundColor: "rgba(0,0,0,0.55)",
                "&:hover": { color: "#fff", backgroundColor: "rgba(0,0,0,0.75)" },
              }}
            >
              <CloseIcon sx={{ fontSize: FONT_SIZE_PX.subtitle }} />
            </IconButton>
          </MDBox>

          {/* Core Speedometer */}
          <RealisticSpeedometer value={speed} max={200} size={gaugeSize} />

          {/* Bottom Container: Ignition Badge */}
          <MDBox display="flex" flexDirection="column" alignItems="center" mt={2} gap={1}>
            <MDBox
              sx={{
                padding: "6px 16px",
                borderRadius: `${RADIUS.pill}px`,
                border: `1.5px solid ${isIgnitionOn ? BRAND.success : BRAND.danger}`,
                display: "flex",
                alignItems: "center",
                gap: "8px",
                background: "rgba(0,0,0,0.7)",
                backdropFilter: "blur(4px)",
              }}
            >
              <PowerSettingsNewIcon
                sx={{
                  fontSize: FONT_SIZE_PX.subtitle,
                  color: isIgnitionOn ? BRAND.success : BRAND.danger,
                }}
              />
              <span
                style={{
                  fontSize: `${FONT_SIZE_PX.caption}px`,
                  fontWeight: 700,
                  color: isIgnitionOn ? BRAND.success : BRAND.danger,
                  letterSpacing: "0.5px",
                }}
              >
                {isIgnitionOn ? "IGNITION ON" : "IGNITION OFF"}
              </span>
            </MDBox>
          </MDBox>
        </MDBox>
      ) : (
        /* Minimized State */
        <MDBox
          onClick={() => setIsOpen(true)}
          sx={{
            width: isMobile ? 48 : 60,
            height: isMobile ? 48 : 60,
            background: "#111",
            borderRadius: "50%",
            display: "flex",
            alignItems: "center",
            justifyContent: "center",
            cursor: "pointer",
            border: "2px solid #555",
            boxShadow: SHADOW.lg,
            transition: "transform 0.2s ease, border-color 0.2s ease",
            "&:hover": { transform: "scale(1.05)", borderColor: "#888" },
          }}
        >
          <SpeedIcon sx={{ color: "#fff", fontSize: isMobile ? 22 : 28 }} />
        </MDBox>
      )}
    </MDBox>
  );
};

export default VehicleDashboard;
